/* Service-worker levenscyclus.

   Eén harde regel: de app herlaadt zichzelf nooit uit zichzelf. Een wachtende
   worker wordt alleen geactiveerd wanneer de gebruiker daar expliciet om
   vraagt. Gebeurt dat niet, dan neemt de eerstvolgende gewone appstart de
   nieuwe versie vanzelf over — precies zoals een browser dat zonder service
   worker ook zou doen.

   Waarom: op WebKit veroorzaakte `controllerchange → location.reload()` een
   volledige main-frame-herlaad midden in een camerareis of een open kamer.
   Dat verloor navigatiestate, focus en soms het WebGL-context. */

type Listener = (ready: boolean) => void;
type RefusalListener = (reason: RefusalReason | null) => void;
/** Waarom een gevraagde update nu niet doorgaat. */
export type RefusalReason = "invoer" | "outbox" | "redeem" | "onbekend";

const listeners = new Set<Listener>();
const refusalListeners = new Set<RefusalListener>();
let ready = false;
let refusal: RefusalReason | null = null;
let reg: ServiceWorkerRegistration | null = null;
/* Alleen een echte gebruikersklik zet deze vlag. De controllerchange-handler
   weigert zonder deze vlag te herladen, ook als een ánder tabblad de worker
   laat wisselen. */
let userAsked = false;

const redeeming = () =>
  location.hash.includes("k=") || sessionStorage.getItem("gorodn_redeeming") === "1";

const publish = (next: boolean) => {
  if (next === ready) return;
  ready = next;
  for (const fn of listeners) fn(ready);
};

/** Abonneer op «er staat een update klaar». Roept direct de huidige stand aan. */
export function onUpdateReady(fn: Listener): () => void {
  listeners.add(fn);
  fn(ready);
  return () => { listeners.delete(fn); };
}

/** Abonneer op «de update kon nu niet doorgaan», met reden. */
export function onUpdateRefused(fn: RefusalListener): () => void {
  refusalListeners.add(fn);
  fn(refusal);
  return () => { refusalListeners.delete(fn); };
}
const publishRefusal = (reason: RefusalReason | null) => {
  refusal = reason;
  for (const fn of refusalListeners) fn(reason);
};

/* Activeren mag alleen wanneer er niets onderweg is dat een reload zou
   beschadigen: geen lopende redeem, geen open invoer, geen wachtende
   mutaties in de outbox. */
async function safeToActivate(): Promise<RefusalReason | null> {
  if (redeeming()) return "redeem";
  const el = document.activeElement;
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement ||
      (el instanceof HTMLElement && el.isContentEditable)) return "invoer";
  try {
    const { outboxCount } = await import("./outbox");
    return (await outboxCount()) === 0 ? null : "outbox";
  } catch {
    return "onbekend";                   // outbox onleesbaar: niet activeren
  }
}

/** Uitsluitend aan te roepen vanuit een expliciete gebruikersactie.
    Resolvet false als de update nu niet veilig door kan. */
export async function applyUpdate(): Promise<boolean> {
  if (!reg?.waiting) { publishRefusal("onbekend"); return false; }
  const why = await safeToActivate();
  if (why) { publishRefusal(why); return false; }
  const waiting = reg.waiting;           // hercheck ná de await
  if (!waiting) { publishRefusal("onbekend"); return false; }
  publishRefusal(null);
  userAsked = true;
  waiting.postMessage("SKIP_WAITING_OK");
  return true;
}

export function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator) || redeeming()) return;

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!userAsked) {
      /* Een ánder tabblad heeft de update doorgevoerd. Niet herladen — maar
         de knop hier is nu zinloos, want de wachtende worker bestaat niet
         meer. Laat hem verdwijnen in plaats van dood achterblijven. */
      void navigator.serviceWorker.getRegistration()
        .then(r => publish(!!r?.waiting && !redeeming()))
        .catch(() => publish(false));
      return;
    }
    userAsked = false;
    location.reload();
  });

  /* Eén melding bij het laden van dít document. Bewust NIET bij
     controllerchange: een venster dat door een nieuwe worker wordt geclaimd
     is juist niet vers -- het draait nog de oude code. */
  /* Meld welke entry dit venster draait. Die URL is gehasht en identificeert
     dus de build; de worker houdt daarmee precies de caches die nog in gebruik
     zijn, in plaats van blind een vast aantal builds. */
  const entryUrl = (): string | null => {
    const own = document.querySelector<HTMLScriptElement>('script[type="module"][src]');
    return own?.src ? new URL(own.src, location.href).pathname : null;
  };
  const announceEntry = (): void => {
    const url = entryUrl();
    if (!url) return;
    /* Naar de ACTIEVE worker, niet naar `controller`: bij de allereerste load
       is dit venster nog niet geclaimd en is `controller` null -- dan zou de
       melding stil verdwijnen. */
    void navigator.serviceWorker.ready
      .then(reg => reg.active?.postMessage({ type: "CLIENT_ENTRY", url }))
      .catch(() => { /* geen worker: niets te melden */ });
  };
  announceEntry();
  navigator.serviceWorker.addEventListener("controllerchange", announceEntry);

  /* Basisbewust: productie registreert `/sw.js` met scope `/`, de next-skin
     `/next/sw.js` met scope `/next/`. Twee workers op één origin, elk over
     zijn eigen pad. */
  const base = import.meta.env.BASE_URL;
  navigator.serviceWorker.register(`${base}sw.js`, { scope: base }).then((r) => {
    reg = r;
    const check = () => publish(!!r.waiting && !redeeming());
    check();
    r.addEventListener("updatefound", () => {
      /* Lokale referentie: `r.installing` is bij het statechange-moment al
         doorgeschoven naar waiting/activated. */
      const installing = r.installing;
      installing?.addEventListener("statechange", check);
    });
    const poll = () => { void r.update().then(check).catch(() => { /* offline */ }); };
    setInterval(poll, 30 * 60_000);
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) poll();
    });
  }).catch(() => {
    /* Registratie kan falen (privémodus, quota, ingetrokken toestemming).
       De app werkt zonder service worker volledig; stil doorgaan is hier het
       juiste gedrag — maar wel expliciet, niet als verloren rejection. */
  });
}
